import React from "react";
import { useResponsive } from "@outplayed/responsive";
import { useWowPath } from "../../../hooks/path-hooks";
import {
  EXPANSION,
  PATCH,
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getFormattedContentType,
  getFormattedNames,
  getIcyVeinsLinkName,
  getIsHealer,
  getIsTank,
  getDataHeroName,
  getHeroNameFromSelectedTree,
  pickBetterHeroSide,
} from "../../../lib/wowHelpers";
import NoData from "../../Common/NoData";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import StatPriorities from "../Builds/StatPriorities";
import TalentTreeSection from "../Builds/TalentTreeSection";
import { validParams } from "../../../query_parameters/valid_parameters";
import DataDetails from "../../Common/DataDetails";
import PvPStatSection from "./PvPStatSection";
import PvPTalents from "./PvPTalents";
import TierSetContainer from "../Builds/TierSetContainer";
import { DateTime } from "luxon";
import Layout from "../Layout";
import PvEGearGuide from "../../GearGuide/PvEGearGuide";
import BestEnchants from "../Combos/BestEnchants";
import GemEnchantContainer from "../Combos/GemEnchantContainer";
import EmbellishmentPairs from "../Combos/EmbellishmentPairs";
import GearContainer, { getPrimaryMetric, ItemsSubContainerCategory } from "../Combos/GearContainer";
import TrinketsContainer from "../Combos/TrinketsContainer";
import SubtitleAndDescription from "../../Common/SubtitleAndDescription";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { WoWContext } from "../../App";
import { useHeroQueryParams } from "hooks/wow/heroQueryParams";
import HeroTalentSelector from "../Builds/HeroTalentSelector";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";
import { WOW_TREE_DATA_PATH } from "lib/constants";
import { BuildsPageSkeleton } from "components/Common/PageSkeletons";

// pvp_v2 combo entries carry the per-item variant fields (bonus ids, gems,
// enchant id, crafted quality) nested under a pvp sub-object; the gear guide
// reads them flat like the PvE payload, so merge them up.
const flattenComboVariants = (combos) => {
  if (!combos) return combos;
  return Object.fromEntries(
    Object.entries(combos).map(([slotPair, itemTypes]) => [
      slotPair,
      Object.fromEntries(
        Object.entries(itemTypes ?? {}).map(([itemType, entries]) => [
          itemType,
          (entries ?? []).map((entry) => ({ ...entry, ...(entry?.pvp ?? {}) })),
        ]),
      ),
    ]),
  );
};

// pvp_v2 table rows keep their metrics (rating, max_rating) in a pvp
// sub-object; the shared tables read them flat, so merge them up.
const flattenPvpMetrics = (rows) => (rows ?? []).map((row) => ({ ...row, ...(row?.pvp ?? {}) }));

const PvPTalentsContainer = () => {
  const { currentBreakpoint } = useResponsive();
  const isMobile =
    currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM" || currentBreakpoint === "MOBILE_LARGE";

  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const { gameMode } = useQueryString();
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  // pvp_v2 mirrors the PvE per-class file layout, keyed [pvp][all][Class-Spec-Hero]
  const pvpV2Root = `${API_WOW_ROOT_PATH}/pvp_v2/${gameMode ?? "3v3"}/${dataClassName}`;
  const { data: buildsTable, loading: isLoading } = useJSONFetcher(`${pvpV2Root}/${dataClassName}_builds.json`, {
    ssr: true,
  });
  const { data: gemTable, loading: isGemLoading } = useJSONFetcher(`${pvpV2Root}/${dataClassName}_gems.json`, {
    ssr: true,
  });
  const { data: enchantTable, loading: isEnchantLoading } = useJSONFetcher(`${pvpV2Root}/${dataClassName}_enchants.json`, {
    ssr: true,
  });
  const { data: gearTable, loading: isGearLoading } = useJSONFetcher(`${pvpV2Root}/${dataClassName}_itemsTable.json`, {
    ssr: true,
  });
  const { data: trinketsTable, loading: isTrinketsLoading } = useJSONFetcher(`${pvpV2Root}/${dataClassName}_trinkets.json`, {
    ssr: true,
  });

  const pvpEntries = buildsTable?.pvp?.all;

  const getHeroSideRanking = (side) => {
    const heroName = getDataHeroName(getHeroNameFromSelectedTree(side, displayClassName, displaySpecName));
    const stats = pvpEntries?.[`${dataClassName}-${dataSpecName}-${heroName}`]?.stats;
    return { tierLetter: stats?.tier_letter, score: stats?.pvp?.max_rating, parses: stats?.count };
  };
  const defaultHero = pvpEntries ? pickBetterHeroSide(getHeroSideRanking("left"), getHeroSideRanking("right")) : null;
  const { selectedHero, setSelectedHero } = useHeroQueryParams(displayClassName, displaySpecName, defaultHero);
  const dataHeroName = getDataHeroName(getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName));
  const dataOtherHeroName = getDataHeroName(
    getHeroNameFromSelectedTree(selectedHero === "left" ? "right" : "left", displayClassName, displaySpecName),
  );
  const heroSpecName = getHeroNameFromSelectedTree(selectedHero, displayClassName, displaySpecName);

  const specs = validParams.builds.specs[classType];
  const allTrees = {};
  for (let i = 0; i < 4; i++) {
    const { data: specData } = useJSONFetcher(`${WOW_TREE_DATA_PATH}/${classType}_${specs[i]?.value}.json`, {
      ssr: true,
    });
    allTrees[specs[i]?.value] = specData;
  }

  const classAndSpecTree = allTrees[specType];

  const pageDescription = `Everything for ${displaySpecName} ${displayClassName} PvP. The best ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(
    displayClassName,
  )} hero talents, from BIS Gear to stat priorities, data-driven by top players. ${EXPANSION} ${PATCH}.`;

  const metaDescription = `The Best ${displaySpecName} ${displayClassName} PvP talents, from gear to stat priority with the new ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]} and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} hero talents, in ${EXPANSION}.`;

  const layoutProps = {
    className: "wow-pvp-container",
    classType,
    filterPage: "pvp",
    metaDescription,
    metaTitle: `${displaySpecName} ${displayClassName} Hero Talents for PvP - BIS Stats, Talents and Gear`,
    pageDescription,
    pageTitle: `PvP Hero Talents, Stats, and Gear`,
    specType,
  };

  if (isLoading || isGemLoading || isEnchantLoading || isGearLoading || isTrinketsLoading) {
    return (
      <Layout {...layoutProps}>
        <BuildsPageSkeleton />
      </Layout>
    );
  }

  const classSpecHero = `${dataClassName}-${dataSpecName}-${dataHeroName}`;
  const buildsData = pvpEntries?.[classSpecHero];
  const otherHeroBuildsData = pvpEntries?.[`${dataClassName}-${dataSpecName}-${dataOtherHeroName}`];
  const statPriorities = buildsData?.stat_priorities?.stat_priorities?.split("-").filter(Boolean) ?? [];

  const talentTreeSectionData = {
    classType,
    mainStat: buildsData?.talents?.pvp?.max_rating ?? 0,
    popularity: parseFloat(buildsData?.talents?.perc ?? 0).toFixed(1) + "%",
    selectedTalents: buildsData?.talents?.talent?.split("-") ?? [],
    classAndSpecTree,
    specType,
    statLabel: "Max Rating",
    statTooltip: "The highest rated player that used this particular talent build in the selected competitive PvP game mode",
    allTrees,
    encounterType: "PvP",
    selectedHero: selectedHero,
  };

  const toHeroStats = (entry) => ({
    pickrate: (entry?.stats?.pickrate ?? 0) * 100,
    rating: entry?.stats?.pvp?.rating ?? 0,
    parses: entry?.stats?.count ?? 0,
    tierLetter: entry?.stats?.tier_letter ?? "",
  });

  const topSectionData = {
    dataClassName: dataClassName,
    dataSpecName: dataSpecName,
    chosenHero: toHeroStats(buildsData),
    otherHero: toHeroStats(otherHeroBuildsData),
    specType: specType,
  };

  const pvpContextValue = {
    allTrees,
    selectedTalents: buildsData?.talents?.talent?.split("-") ?? [],
    specType,
    selectedHero,
  };

  const hasData = (buildsData?.stats?.pvp?.played ?? 0) > 0;

  const classSpecGems = gemTable?.pvp?.all?.[classSpecHero]?.gems;
  const classSpecEnchants = enchantTable?.pvp?.all?.[classSpecHero];
  const hasGemData = (classSpecGems?.gem_counts?.length ?? 0) > 0;
  const hasEnchantData = Object.values(classSpecEnchants?.enchants ?? {}).some((slot) => Object.keys(slot).length > 0);

  const gearTableEntry = gearTable?.pvp?.all?.[classSpecHero]?.items_table;
  const classSpecGear = gearTableEntry && {
    ...gearTableEntry,
    items: Object.fromEntries(
      Object.entries(gearTableEntry.items ?? {}).map(([slot, slotData]) => [
        slot,
        { ...slotData, items: flattenPvpMetrics(slotData?.items) },
      ]),
    ),
  };
  const hasGearTables = (classSpecGear?.items?.belt?.total_count ?? 0) > 0;

  // The backend publishes crafted gear with bonus IDs upgraded to the max
  // crafting rank (the rank an optimizing player would buy), so tooltips carry
  // quality tier 5. Each list is capped to the most popular entries; the long
  // tail is sub-1% noise.
  const craftedGear = flattenPvpMetrics(gearTableEntry?.crafted_gear)
    .map((item) => ({ ...item, crafted_quality: 5 }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);
  const embellishmentPairs = (gearTableEntry?.embellishment_pairs ?? []).slice(0, 5);
  const hasCraftedData = craftedGear.length > 0 || embellishmentPairs.length > 0;

  const trinketsEntry = trinketsTable?.pvp?.all?.[classSpecHero]?.trinkets;
  const trinkets = flattenPvpMetrics(trinketsEntry?.trinket_combos);
  const trinketPairs = (trinketsEntry?.trinket_combos2 ?? []).map((pairList) => flattenPvpMetrics(pairList));
  const hasTrinketData = (trinketsEntry?.total ?? 0) > 0;

  const isHealer = getIsHealer(displaySpecName);
  const isTank = getIsTank(displaySpecName);
  const contentType = getFormattedContentType("PvP");
  const specClass2 = `${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(displayClassName)}`;
  const pvpMetric = getPrimaryMetric(displaySpecName, "PvP");

  return (
    <Layout {...layoutProps}>
      {hasData ? (
        <>
          <WoWContext.Provider value={pvpContextValue}>
            <HeroTalentSelector
              displayClassName={displayClassName}
              displaySpecName={displaySpecName}
              activeHero={selectedHero}
              onClick={setSelectedHero}
              specType={specType}
              pageType="PvP"
              data={topSectionData}
            />
            <DataDetails
              encounterType="PvP"
              numLogsTitle="Top Rated Players Analyzed"
              timestamp={DateTime.fromISO(buildsTable?.timestamp ?? "").toRelative()}
              parses={buildsData?.stats?.count ?? 0}
              ratingRange={`${buildsData?.stats?.pvp?.min_rating ?? 0}-${buildsData?.stats?.pvp?.max_rating ?? 0}`}
              useDetailedTitle
            />
            <PvPStatSection pvpData={{ stats: buildsData?.stats?.pvp }} />
            <StatPriorities
              statPriorities={statPriorities}
              encounterType="PvP"
              dataClassName={dataClassName}
              dataSpecName={dataSpecName}
            />
            <TalentTreeSection {...talentTreeSectionData} />
            <PvPTalents
              displayClassName={displayClassName}
              displaySpecName={displaySpecName}
              pvpTalents={buildsData?.pvp?.pvp_talents ?? []}
            />
            <PvEGearGuide
              builds={buildsData?.items}
              combos={flattenComboVariants(buildsData?.combos)}
              classType={classType}
              specType={specType}
              encounterType="PvP"
            />
            <TierSetContainer
              className={classType}
              specName={specType}
              heroSpecName={heroSpecName}
              encounterType="PvP"
              tierSet={buildsTable?.tier_set?.[specType]}
            />
            {hasCraftedData && (
              <>
                <div className="seperator" />
                <SubtitleAndDescription
                  subtitle={`${specClass2} Best Crafted Items for ${contentType}`}
                  description={`The most popular crafted items and embellishment combinations for ${contentType}.`}
                />
                <div className="wow-items-page crafted-items-container">
                  {craftedGear.length > 0 && (
                    <ItemsSubContainerCategory
                      category="Crafted Items"
                      metricOneName={pvpMetric}
                      metricTwoName="Popularity"
                      data={craftedGear}
                      count={craftedGear.reduce((acc, x) => acc + x.count, 0)}
                    />
                  )}
                  {embellishmentPairs.length > 0 && (
                    <EmbellishmentPairs data={embellishmentPairs} builds={buildsData?.items} slotItems={classSpecGear?.items} />
                  )}
                </div>
              </>
            )}
            {hasGearTables && (
              <>
                <div className="seperator" />
                <SubtitleAndDescription
                  subtitle={`${specClass2} Gear Tables for ${contentType}`}
                  description={`These tables are a comparison of the most popular items in each gear slot for ${contentType}.`}
                />
                <GearContainer
                  classSpecGear={classSpecGear}
                  isMobile={isMobile}
                  displaySpecName={displaySpecName}
                  encounterType="PvP"
                />
              </>
            )}
            {hasTrinketData && (
              <>
                <div className="seperator" />
                <SubtitleAndDescription
                  subtitle={`The Best ${specClass2} Trinkets for ${contentType}`}
                  description={`The most popular trinkets and trinket combinations used by top rated ${displaySpecName} ${displayClassName} players.`}
                />
                <TrinketsContainer
                  trinkets={trinkets}
                  trinketPairs={trinketPairs}
                  totalTrinkets={trinketsEntry?.total}
                  isMobile={isMobile}
                  isHealer={isHealer}
                  isTank={isTank}
                  encounterType="PvP"
                />
              </>
            )}
            {hasEnchantData && (
              <>
                <div className="seperator" />
                <BestEnchants
                  displayClassName={displayClassName}
                  displaySpecName={displaySpecName}
                  enchants={classSpecEnchants?.enchants}
                  contentType={contentType}
                />
              </>
            )}
            {hasGemData && hasEnchantData && (
              <GemEnchantContainer
                classSpecGems={classSpecGems}
                classSpecEnchants={classSpecEnchants}
                encounterType="PvP"
                displayClassName={displayClassName}
                displaySpecName={displaySpecName}
              />
            )}
            <PageSectionCrossLinks
              classType={classType}
              description={`Our friends at Icy Veins have you covered, with an in-depth guide to ready your ${displaySpecName} ${displayClassName} for PvP.`}
              guideData={[
                {
                  description: `The best in-depth guides covering PvP for ${displaySpecName} ${displayClassName}`,
                  link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                    classType,
                  )}-pvp-guide`,
                  title: "PvP Guide",
                },
              ]}
              specType={specType}
            />
          </WoWContext.Provider>
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default PvPTalentsContainer;
